/**
 * Certification Module - Onboarding completion certificate
 *
 * Features:
 * - ULID certificate ID generation
 * - Visual certificate display
 * - Download/print functionality
 * - Completion summary
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Award,
  Printer,
  CheckCircle2,
  Shield,
  BookOpen,
  ClipboardCheck,
  Sparkles,
  Copy,
  Check,
} from 'lucide-react';
import type { OnboardingRole } from '../../types/onboarding';
import {
  useOnboardingStore,
  generateCertificateId,
  useCertificate,
} from '../../stores/onboardingStore';
import { CURRENT_ONBOARDING_VERSION } from '../../lib/onboarding/constants';
import { StepCard } from './OnboardingStep';

interface CertificationModuleProps {
  onComplete: () => void;
  role: OnboardingRole;
}

export default function CertificationModule({
  onComplete,
  role,
}: CertificationModuleProps) {
  const [certificateGenerated, setCertificateGenerated] = useState(false);
  const [copied, setCopied] = useState(false);

  const { markComplete, profileData } = useOnboardingStore();
  const certificate = useCertificate();

  // Generate certificate on mount if not already generated
  useEffect(() => {
    if (!certificate.certificateId && !certificateGenerated) {
      const newCertId = generateCertificateId();
      markComplete(newCertId);
      setCertificateGenerated(true);
    }
  }, [certificate.certificateId, certificateGenerated, markComplete]);

  // Format completion date
  const completionDate = useMemo(() => {
    const date = certificate.completedAt
      ? new Date(certificate.completedAt)
      : new Date();
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, [certificate.completedAt]);

  // Copy certificate ID
  const handleCopy = useCallback(async () => {
    if (!certificate.certificateId) return;
    try {
      await navigator.clipboard.writeText(certificate.certificateId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = certificate.certificateId;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [certificate.certificateId]);

  // Print certificate
  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  // Get role display name
  const roleDisplayName = useMemo(() => {
    switch (role) {
      case 'internal_core':
        return 'Core Team Member';
      case 'research_collab':
        return 'Research Collaborator';
      case 'contractor':
        return 'Contractor';
      case 'guest':
        return 'Guest';
      default:
        return 'Team Member';
    }
  }, [role]);

  // Completion stats
  const completionStats = [
    {
      icon: ClipboardCheck,
      label: 'Legal Agreements',
      value: 'Signed',
      color: 'var(--color-success)',
    },
    {
      icon: Shield,
      label: 'Security Training',
      value: 'Passed',
      color: 'var(--color-success)',
    },
    {
      icon: BookOpen,
      label: 'System Education',
      value: role === 'guest' ? 'N/A' : 'Completed',
      color: role === 'guest' ? 'var(--color-text-muted)' : 'var(--color-success)',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Success Banner */}
      <div
        className="p-6 rounded-xl text-center"
        style={{
          background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(59, 130, 246, 0.15) 100%)',
          border: '1px solid var(--color-success)',
        }}
      >
        <div
          className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, var(--color-success) 0%, var(--color-accent) 100%)',
            boxShadow: '0 0 40px rgba(34, 197, 94, 0.3)',
          }}
        >
          <Award className="w-10 h-10 text-white" />
        </div>
        <h2
          className="text-2xl font-bold mb-2"
          style={{ color: 'var(--color-text-primary)' }}
        >
          Congratulations!
        </h2>
        <p
          className="text-sm max-w-md mx-auto"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          You have successfully completed the LUKHAS AI onboarding program.
          Your certificate has been generated and recorded.
        </p>
      </div>

      {/* Certificate Card */}
      <div
        id="certificate"
        className="relative p-8 rounded-2xl overflow-hidden print:shadow-none"
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          border: '2px solid var(--color-accent)',
        }}
      >
        {/* Decorative Elements */}
        <div
          className="absolute top-0 right-0 w-64 h-64 opacity-5"
          style={{
            background: 'radial-gradient(circle, var(--color-accent) 0%, transparent 70%)',
          }}
        />
        <div
          className="absolute bottom-0 left-0 w-48 h-48 opacity-5"
          style={{
            background: 'radial-gradient(circle, var(--color-success) 0%, transparent 70%)',
          }}
        />

        {/* Certificate Content */}
        <div className="relative text-center">
          {/* Logo */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <Sparkles className="w-6 h-6" style={{ color: 'var(--color-accent)' }} />
            <span className="lambda-brand text-3xl tracking-wider">
              <span style={{ color: 'var(--color-text-primary)' }}>LUKH</span>
              <span style={{ color: 'var(--color-accent)' }}>Λ</span>
              <span style={{ color: 'var(--color-text-primary)' }}>S </span>
              <span style={{ color: 'var(--color-accent)' }}>Λ</span>
              <span style={{ color: 'var(--color-text-primary)' }}>I</span>
            </span>
            <Sparkles className="w-6 h-6" style={{ color: 'var(--color-accent)' }} />
          </div>

          {/* Title */}
          <h3
            className="text-xl font-semibold mb-1"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Certificate of Completion
          </h3>
          <h2
            className="text-2xl font-bold mb-6"
            style={{ color: 'var(--color-text-primary)' }}
          >
            Onboarding Training Program
          </h2>

          {/* Divider */}
          <div
            className="w-32 h-1 mx-auto mb-6 rounded-full"
            style={{ backgroundColor: 'var(--color-accent)' }}
          />

          {/* Recipient */}
          <p
            className="text-sm mb-2"
            style={{ color: 'var(--color-text-muted)' }}
          >
            This certifies that
          </p>
          <p
            className="text-2xl font-bold mb-2"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {profileData.displayName || 'Team Member'}
          </p>
          <p
            className="text-sm mb-6"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {roleDisplayName}
          </p>

          {/* Description */}
          <p
            className="text-sm max-w-lg mx-auto mb-6"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            has successfully completed all required training modules including
            legal agreements, security awareness training, and system education
            for the LUKHAS AI platform.
          </p>

          {/* Certificate Details */}
          <div
            className="inline-flex items-center gap-6 p-4 rounded-xl mb-6"
            style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
          >
            <div className="text-left">
              <p
                className="text-xs font-medium"
                style={{ color: 'var(--color-text-muted)' }}
              >
                Certificate ID
              </p>
              <div className="flex items-center gap-2">
                <p
                  className="font-mono font-semibold"
                  style={{ color: 'var(--color-accent)' }}
                >
                  {certificate.certificateId || 'Generating...'}
                </p>
                <button
                  onClick={handleCopy}
                  className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors print:hidden"
                  title="Copy certificate ID"
                >
                  {copied ? (
                    <Check className="w-4 h-4" style={{ color: 'var(--color-success)' }} />
                  ) : (
                    <Copy className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
                  )}
                </button>
              </div>
            </div>
            <div
              className="w-px h-10"
              style={{ backgroundColor: 'var(--color-border)' }}
            />
            <div className="text-left">
              <p
                className="text-xs font-medium"
                style={{ color: 'var(--color-text-muted)' }}
              >
                Date Issued
              </p>
              <p
                className="font-semibold"
                style={{ color: 'var(--color-text-primary)' }}
              >
                {completionDate}
              </p>
            </div>
            <div
              className="w-px h-10"
              style={{ backgroundColor: 'var(--color-border)' }}
            />
            <div className="text-left">
              <p
                className="text-xs font-medium"
                style={{ color: 'var(--color-text-muted)' }}
              >
                Version
              </p>
              <p
                className="font-semibold"
                style={{ color: 'var(--color-text-primary)' }}
              >
                {CURRENT_ONBOARDING_VERSION}
              </p>
            </div>
          </div>

          {/* Signature Line */}
          <div className="flex justify-center gap-12 mt-8">
            <div className="text-center">
              <div
                className="w-32 h-0.5 mb-2"
                style={{ backgroundColor: 'var(--color-border)' }}
              />
              <p
                className="text-xs"
                style={{ color: 'var(--color-text-muted)' }}
              >
                LUKHAS AI Platform
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Completion Stats */}
      <div className="grid grid-cols-3 gap-4">
        {completionStats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.label}
              className="p-4 rounded-xl text-center"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border)',
              }}
            >
              <div
                className="w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${stat.color}20` }}
              >
                <Icon className="w-5 h-5" style={{ color: stat.color }} />
              </div>
              <p
                className="text-xs mb-1"
                style={{ color: 'var(--color-text-muted)' }}
              >
                {stat.label}
              </p>
              <p
                className="font-semibold text-sm"
                style={{ color: stat.color }}
              >
                {stat.value}
              </p>
            </div>
          );
        })}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 justify-center print:hidden">
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            color: 'var(--color-text-secondary)',
            border: '1px solid var(--color-border)',
          }}
        >
          <Printer className="w-4 h-4" />
          Print
        </button>
        <button
          onClick={handleCopy}
          className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            color: 'var(--color-text-secondary)',
            border: '1px solid var(--color-border)',
          }}
        >
          {copied ? (
            <>
              <Check className="w-4 h-4" style={{ color: 'var(--color-success)' }} />
              Copied!
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              Copy ID
            </>
          )}
        </button>
      </div>

      {/* Continue to Dashboard */}
      <StepCard variant="highlight">
        <div className="text-center">
          <CheckCircle2
            className="w-12 h-12 mx-auto mb-4"
            style={{ color: 'var(--color-success)' }}
          />
          <h3
            className="font-bold text-lg mb-2"
            style={{ color: 'var(--color-text-primary)' }}
          >
            You're All Set!
          </h3>
          <p
            className="text-sm mb-6"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Your onboarding is complete. You now have full access to the Team Portal.
            Your certificate has been saved and can be viewed anytime from your profile.
          </p>
          <button
            onClick={onComplete}
            className="px-8 py-3 rounded-xl font-semibold transition-all hover:scale-105"
            style={{
              backgroundColor: 'var(--color-accent)',
              color: 'var(--color-bg)',
            }}
          >
            Go to Dashboard
          </button>
        </div>
      </StepCard>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #certificate, #certificate * {
            visibility: visible;
          }
          #certificate {
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            width: 100%;
            max-width: 800px;
            border: none !important;
          }
        }
      `}</style>
    </div>
  );
}
